<template>
  <div class="__filters" :class="[open ? 'open' : '', className, type, { dark }, $config.project]">
    <button class="filter-button" @click="open = !open">
      <template v-if="type === 'sort'">
        <IconSort class="icon-sort" />
        <span>
          Sort
        </span>
      </template>
      <template v-else-if="type === 'filter-sort'">
        <IconFilterSort class="icon" />
      </template>
      <template v-else-if="type === 'filter-icon'">
        <IconFilter class="icon" />
      </template>
      <template v-else>
        <IconFilter class="icon" />
        <span>
          Filter
        </span>
      </template>
      <div v-if="appliedCounter && counter > 0" class="applied-counter">
        {{ counter }}
      </div>
    </button>
    <div class="__filters-dropdown">
      <div class="__filters-dropdown-body">
        <div
          v-for="(filter, idx) of f"
          :key="idx"
          class="collapsed"
          :class="filter.type"
        >
          <div class="filter-shown" @click="collapse($event)">
            {{ filter.name }}
            <div class="arrow-down" />
          </div>
          <div v-if="filter.type === 'text'" class="options">
            <input
              type="text"
              :placeholder="filter.values[idx].text"
              :value="filter.values[idx].value"
              :name="idx"
              @input="input"
            >
          </div>
          <div v-else-if="filter.type === 'range'" class="options range-options">
            <div>
              <input
                v-model="filter.values[idx].value[0]"
                type="text"
                class="min-range-input"
                @keydown="validateNumber"
              >
              <input
                v-model="filter.values[idx].value[1]"
                type="text"
                class="max-range-input"
                @keydown="validateNumber"
                @keyup="validateMaxRange(idx, $event)"
              >
            </div>
            <div class="range-slider-wrapper">
              <client-only>
                <VueSlider
                  v-model="filter.values[idx].value"
                  :enable-cross="false"
                  tooltip="none"
                  :lazy="true"
                  :min="filter.values[idx].min"
                  :max="filter.values[idx].max"
                  :interval="filter.values[idx].step"
                  @change="rangeChange(idx, $event)"
                />
              </client-only>
            </div>
          </div>
          <ul v-else :class="{ 'show-more': showMore(filter.values) }">
            <li
              v-for="(v, k) of filter.values"
              :key="k"
              :class="{ selected: v.selected, first: v.first, last: v.last, italic: v.italic, 'not-capitalized': idx === 'duration' }"
              :style="{ order: v.i }"
              @click="selectOption(idx, k)"
            >
              <img v-if="v.image" :src="v.image">
              <span>{{ v.text }}</span>
            </li>
          </ul>
          <div v-if="showMore(filter.values)" class="show" @click="expand($event)" />
        </div>
      </div>
      <div v-if="showClear || type !== 'sort' || $store.state.layout === 'mobile'" class="__filters-dropdown-footer">
        <button class="btn-primary" @click="apply">
          Apply
        </button>
        <div class="clear-btn" @click="clean">
          <IconTrash /> Clear all
        </div>
      </div>
      <a class="arrow" @click="open = false">
        <div class="arrow-svg" />
      </a>
    </div>
  </div>
</template>

<script>
import { cloneDeep, debounce } from 'lodash-es';

import 'vue-slider-component/theme/default.css';

import IconFilter from '../assets/svg/icon-filter.svg?vue-component';
import IconSort from '../assets/svg/icon-sort.svg?vue-component';
import IconFilterSort from '../assets/svg/icon-filter-sort.svg?vue-component';
import IconTrash from '../assets/svg/icon-trash.svg?vue-component';

export default {
  components: {
    IconFilter,
    IconSort,
    IconFilterSort,
    IconTrash,
    // eslint-disable-next-line consistent-return
    VueSlider: () => {
      if (process.client) {
        return import('vue-slider-component');
      }
    },
  },
  props: {
    filters: { type: Object, required: true },
    className: { type: String, default: '', required: false },
    type: { type: String, default: '', required: false },
    showClear: { type: Boolean, required: false },
    appliedCounter: { type: Boolean, required: false },
    dark: { type: Boolean, required: false },
    coupled: { type: Boolean, required: false },
    deSelected: { type: Object, required: false, default: () => ({}) },
    reset: { type: Boolean, required: true },
  },
  data() {
    return {
      f: cloneDeep(this.filters),
      open: false,
      counter: 0,
      isDeSelected: false,
    };
  },
  computed: {
    appliedFilters() {
      return Object
        .entries(this.f)
        .reduce((p, [__k, __v]) => ({
          ...p,
          [__k]: Object
            .entries(__v.values)
            .filter(([, _v]) => _v.selected || _v.value)
            .map(([_k, _v]) => {
              if (_k !== 'location' && _k !== 'minutes') {
                this.counter += 1;
              }

              if (__v.labelsAsValues) {
                return _v.text;
              }

              if (__v.type === 'text' || __v.type === 'range') {
                return _v.value;
              }

              return _k;
            }),
        }), {});
    },
    filterParams() {
      return Object
        .entries(this.appliedFilters)
        .map(([_k, _v]) => {
          // eslint-disable-next-line no-underscore-dangle
          if (this.f[_k].type === 'multiple') {
            return _v.map((__v) => `filter[${_k}][]=${encodeURIComponent(__v)}`);
          }

          return _v.map((__v) => `filter[${_k}]=${encodeURIComponent(Array.isArray(__v) ? __v.join(',') : __v)}`);
        })
        .flat()
        .join('&');
    },
  },
  watch: {
    filters(v) {
      this.f = cloneDeep(v);
      this.updateSelectedFromQuery();
    },
    open() {
      if (this.$store.state.layout === 'mobile') {
        if (this.open) {
          document.querySelector('body').style.overflow = 'hidden';
        } else {
          document.querySelector('body').style.overflow = 'auto';
        }
      }
    },
    deSelected(s) {
      this.isDeSelected = true;
      this.selectOption(s.type, s.filter);
    },
    reset(r) {
      if (r) {
        this.clean();
      }
    },
  },
  mounted() {
    this.updateSelectedFromQuery();
    this.$root.$on('emit-click', (e) => {
      if (
        !e.target.closest(`.__filters${this.type ? `.${this.type}` : ''}`)
        || e.target.classList.contains(`.__filters${this.type ? `.${this.type}` : ''}`)
      ) {
        this.open = false;
      }
    });
  },
  methods: {
    selectOption(idx, k) {
      const filter = this.f[idx];
      const { selected } = filter.values[k];
      if (filter.type !== 'multiple') {
        Object.keys(this.f[idx].values).filter((f) => f !== k).forEach((f) => {
          this.$set(this.f[idx].values[f], 'selected', false);
        });
      }

      this.$set(this.f[idx].values[k], 'selected', !selected);

      if (this.f[idx].values[k].all) {
        Object.keys(this.f[idx].values).filter((f) => f !== k).forEach((f) => {
          this.$set(this.f[idx].values[f], 'selected', this.f[idx].values[k].selected);
        });
      }

      this.counter = 0;

      if (this.$store.state.layout === 'mobile' && !this.isDeSelected) {
        return;
      }

      this.$emit('change', {
        current: {
          key: idx,
          value: Object.entries(this.f[idx].values).filter(([, v]) => v.selected || v.value).map(([_k]) => _k),
        },
        filter: this.appliedFilters,
        params: this.filterParams,
      });
      this.isDeSelected = false;
    },
    clean() {
      if (!this.coupled) {
        this.f = cloneDeep(this.filters);
      } else {
        Object.keys(this.f).forEach((k) => {
          Object.entries(this.f[k].values).forEach(([_k, _v]) => {
            if ('selected' in _v) {
              // eslint-disable-next-line no-underscore-dangle
              this.f[k].values[_k].selected = false;
            }

            if ('value' in _v && this.f[k].type === 'text') {
              // eslint-disable-next-line no-underscore-dangle
              this.f[k].values[_k].value = '';
            }

            if ('value' in _v && this.f[k].type === 'range') {
              // eslint-disable-next-line no-underscore-dangle
              this.f[k].values[_k].value = [this.f[k].values[_k].min, this.f[k].values[_k].max];
            }
          });
        });

        this.f = cloneDeep(this.f);

        if (this.f.location) {
          this.f.location.values.location.value = '';
        }
      }

      this.counter = 0;

      this.$emit('change', { current: { key: '', value: '' }, filter: this.appliedFilters, params: this.filterParams });
      this.$emit('clean');
    },
    apply() {
      this.$emit('change', { filter: this.appliedFilters, params: this.filterParams });
      this.open = false;
    },
    updateSelectedFromQuery() {
      if (this.$route.query) {
        const params = this.className === 'in-video' ? this.$store.state['qa-hub'].filters : this.$route.query;
        Object.keys(params)
        // eslint-disable-next-line no-prototype-builtins
          .filter((p) => this.f.hasOwnProperty(p))
          .forEach((m) => {
            if (this.f[m].type !== 'multiple') {
              Object.keys(this.f[m].values).forEach((f) => {
                this.$set(this.f[m].values[f], 'selected', false);
              });
            }

            if (!this.f[m].values) {
              return false;
            }

            if (this.f[m].values.all && params[m].split(',').length === Object.keys(this.f[m].values).length - 1) {
              this.$set(this.f[m].values.all, 'selected', true);
            }

            return Object.keys(this.f[m].values).filter((val) => params[m].split(',').includes(val)).map((v) => this.$set(this.f[m].values[v], 'selected', true));
          });
      }
    },
    input: debounce(function input(e) {
      const k = e.target.name;
      const v = e.target.value;

      this.$set(this.f[k].values[k], 'value', v);
    }, 1000),
    rangeChange(idx, v) {
      const [, max] = v;

      if (max <= this.f[idx].values[idx].max) {
        this.$set(this.f[idx].values[idx], 'value', v);
      }
    },
    validateNumber(e) {
      const v = +e.key;

      if (!v && e.key !== 'Backspace' && e.key !== 'Delete' && v !== 0 && e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') {
        e.preventDefault();
      }
    },
    validateMaxRange(idx, e) {
      const { max } = this.f[idx].values[idx];
      if (e.target.value > max) {
        // eslint-disable-next-line no-param-reassign
        e.target.value = max;
        this.$set(this.f[idx].values[idx].value, 1, max);
      }
    },
    showMore(filterData) {
      if (Object.keys(filterData).length > 5) {
        return true;
      }
      return false;
    },
    expand(e) {
      if (e.target.classList.value.includes('show')) {
        e.target.classList.remove('show');
        e.target.classList.add('hide');
        e.target.previousElementSibling.classList.remove('show-more');
      } else {
        e.target.classList.remove('hide');
        e.target.classList.add('show');
        e.target.previousElementSibling.classList.add('show-more');
      }
    },
    collapse(ev) {
      const shown = ev.target.closest('.filter-shown');
      const hidden = ev.target.closest('.filter-hidden');

      if (shown) {
        shown.classList.remove('filter-shown');
        shown.classList.add('filter-hidden');
        shown.parentElement.classList.remove('collapsed');
        shown.parentElement.classList.add('not-collapsed');
      } else {
        hidden.classList.remove('filter-hidden');
        hidden.classList.add('filter-shown');
        hidden.parentElement.classList.remove('not-collapsed');
        hidden.parentElement.classList.add('collapsed');
      }
    },
  },
};
</script>

<style lang="scss">
.__filters {
  position: relative;
  min-width: 100px;
  z-index: 5;

  &.dark {
    color: var(--main-light-shade-4);

    > button {
      border-color: var(--main-light-shade-4);

      svg path[fill] {
        fill: var(--main-light-shade-4);
      }
    }

    .__filters-dropdown {
      background-color: var(--main-dark);

      .__filters-dropdown-body {
        ul {
          &::-webkit-scrollbar-track {
            background: var(--main-dark);
          }

          &::-webkit-scrollbar-thumb {
            background: var(--main-dark-light-3);
          }

          li {
            background: var(--main-dark-light-2);
            color: var(--main-light);
            font-weight: 400;

            &:hover {
              background: var(--main-dark-light-3);
            }

            &.selected {
              background: var(--main-dark-light-4);
              color: var(--main-light);
              font-weight: 500;
            }
          }
        }
      }
    }
  }

  &.main {
    .__filters-dropdown .__filters-dropdown-body div:first-of-type ul {
      width: 327px;
      padding: 0;

      li {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }

  &.in-video {
    .__filters-dropdown {
      right: unset;
      left: 0;
    }
  }

  > button {
    width: 100%;
    color: var(--main-light-shade-4);
    font-weight: 600;
    text-align: center;
    border: 1px solid var(--main-light-shade-4);
    border-radius: 5px;
    height: 40px;
    padding: 8px 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: transparent;
    font-size: 16px;

    span {
      margin-left: 8px;
    }

    svg {
      flex: 0 0 auto;

      path[fill] {
        fill: var(--main-light-shade-4);
      }
    }

    .applied-counter {
      position: absolute;
      top: -8px;
      right: -9px;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 14px;
      font-weight: 500;
      background-color: var(--accent);
      color: var(--main-light);
    }

    &:hover {
      color: var(--main-dark-light-4);
      border-color: var(--main-dark-light-4);

      svg path {
        fill: var(--main-dark-light-4);
        stroke: var(--main-dark-light-4);
      }
    }
  }

  .__filters-dropdown {
    background: var(--main-light);
    padding: 8px 0;
    box-shadow: 0 3px 8px rgba(0, 12, 31, 0.2);
    border-radius: 5px;
    margin-top: 4px;
    display: none;
    position: absolute;
    z-index: 1;
    right: 0;
    font-size: 14px;

    .__filters-dropdown-body {
      display: flex;

      .range-options {
        cursor: unset;
        overflow: unset;

        > div:first-child {
          display: flex;
          justify-content: space-between;
          width: 100%;

          input:not([type='range']) {
            padding: 6px 7px;
            height: 36px;
            max-width: 60px;
            font-size: 14px;
          }
        }

        .range-slider-wrapper {
          margin-top: 12px;
          width: 100%;
          padding: 0 10px;

          .vue-slider {
            height: 8px !important;

            .vue-slider-process {
              background-color: var(--accent);
            }

            .vue-slider-dot-handle {
              background-color: var(--accent);
              box-shadow: none;
            }

            .vue-slider-rail {
              background-color: var(--main-light-shade-2);
            }
          }
        }
      }

      > div {
        padding: 0 16px;
      }

      input {
        height: 40px;
        font-size: 16px;
        border-color: var(--main-light-shade-2);
        margin-top: 8px;
      }

      ul {
        list-style: none;
        margin: 0;
        padding: 0;
        min-width: 168px;
        max-height: 216px;
        margin-top: 8px;
        overflow-y: scroll;
        display: flex;
        flex-wrap: wrap;

        &::-webkit-scrollbar {
          width: 6px;
        }

        &::-webkit-scrollbar-track {
          background: var(--main-light);
        }

        &::-webkit-scrollbar-thumb {
          background: var(--main-light-shade-3);
          border-radius: 100px;
        }

        li {
          padding: 8px 16px;
          background: var(--main-light-shade-2);
          color: var(--main-dark-light-3);
          font-weight: 500;
          border-radius: 5px;
          margin-right: 4px;
          cursor: pointer;
          height: 40px;
          flex: 1 1 100%;
          display: flex;
          align-items: center;

          &:not(.not-capitalized) {
            text-transform: capitalize;
          }

          &:not(:last-child),
          &.first {
            margin-bottom: 4px;
          }

          span {
            display: inline-block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          img {
            width: 15px;
            height: 15px;
            object-fit: cover;
            border: 1px solid var(--main-light);
            margin-right: 4px;
            border-radius: 50%;
          }

          &:hover {
            background: var(--main-light-shade-3);
          }

          &.selected {
            background: var(--accent-light-2);
            color: var(--accent);
            font-weight: 600;
          }

          &.first {
            order: -1;
          }

          &.last {
            order: 100;
          }

          &.italic {
            font-style: italic;
          }
        }
      }
    }

    .__filters-dropdown-footer {
      margin-top: 20px;
      margin-bottom: 8px;
      padding: 0 16px;

      button {
        display: none;
      }

      .btn-primary {
        font-size: 16px;
      }

      .clear-btn {
        display: inline-flex;
        align-items: center;
        color: var(--accent);
        font-weight: 600;
        cursor: pointer;

        svg {
          width: 14px;
          margin-right: 4px;
        }
      }
    }

    .arrow {
      display: none;
    }
  }

  &.site {
    .__filters-dropdown .__filters-dropdown-footer {
      .clear-btn {
        color: var(--main-light-shade-4);

        svg {
          path[fill] {
            fill: var(--main-light-shade-4);
          }
        }
      }
    }
  }

  &.open {
    z-index: 6;

    > button {
      background: var(--main-dark-light-4);
      color: var(--main-light);

      svg path {
        fill: var(--main-light);
        stroke: var(--main-light);
      }
    }

    .__filters-dropdown {
      display: block;
    }
  }
}

@include bp($bp-mobile) {
  .__filters {
    min-width: unset;

    &:before {
      content: '';
      position: fixed;
      top: 64px;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 12, 31, 0.8);
      visibility: hidden;
      opacity: 0;
      transition: all var(--duration) ease;
    }

    > button {
      padding: 14px;
      border-radius: 10px;
      height: 48px;
      width: 48px;

      span {
        display: none;
      }
    }

    .__filters-dropdown {
      max-height: 0;
      padding: 0 var(--spacing-24);
      overflow: hidden;
      transition: all var(--duration) ease;
      top: 64px;
      left: 0;
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      margin-top: 0;
      filter: drop-shadow(0 3px 8px rgba(0, 12, 31, 0.05));
      border-radius: 0 0 10px 10px;
      position: fixed;

      .__filters-dropdown-body {
        width: 100%;
        height: 100%;
        flex-direction: column;
        overflow: auto;
        padding-bottom: var(--spacing-40);
        padding-right: var(--spacing-4);

        > div {
          padding: 0;
          width: 100%;

          &:not(:first-of-type) {
            margin-top: var(--spacing-24);
          }

          ul {
            width: 100% !important;
            max-height: 100%;

            li {
              max-width: 100%;
              height: 52px;
            }
          }
        }

        .single,
        .multiple {
          div:first-of-type {
            width: 100%;
            position: relative;

            svg {
              position: absolute;
              right: 12px;
              top: 50%;
              transform: translateY(-50%);
            }
          }

          &.not-collapsed {
            > *:not(div:first-of-type) {
              display: none;
            }

            .filter-hidden {
              svg {
                transform: rotate(180deg);
              }
            }
          }

          .show-more {
            li {
              display: none;
            }

            li:nth-child(-n+5) {
              display: flex;
            }
          }

          .show,
          .hide {
            line-height: 100%;
            margin-top: var(--spacing-12);
          }

          .show:after {
            content: "Show more";
          }

          .hide:after {
            content: "Show less";
          }

          .show:after,
          .hide:after {
            font-weight: 600;
            font-size: 14px;
            line-height: 110%;
            color: var(--main-light-shade-4);
          }
        }

        .collapsed {
          .filter-shown {
            font-weight: 500;
            font-size: 16px;
            line-height: 150%;
            position: relative;

            .arrow-down {
              display: inline-block;
              border: solid var(--main-light-shade-4);
              border-width: 0 2px 2px 0;
              padding: 2px;
              transform: rotate(-135deg);
              cursor: pointer;
              margin-top: var(--spacing-20);
              width: 10px;
              height: 10px;
              position: absolute;
              right: 12px;
              top: -10px;
            }
          }
        }

        .not-collapsed {
          .filter-hidden {
            font-weight: 500;
            font-size: 16px;
            line-height: 150%;
            position: relative;

            .arrow-down {
              display: inline-block;
              border: solid var(--main-light-shade-4);
              border-width: 0 2px 2px 0;
              padding: 2px;
              transform: rotate(45deg);
              cursor: pointer;
              margin-top: var(--spacing-20);
              width: 10px;
              height: 10px;
              position: absolute;
              right: 12px;
              top: -16px;
            }
          }
        }
      }

      .__filters-dropdown-footer {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        padding: 0;

        button {
          display: block;
          padding: 0;
          width: 100%;
          max-width: 164px;
          height: 40px;
          border-radius: 3px;
        }

        .clear-btn {
          width: 100%;
          max-width: 100px;
          margin-left: var(--spacing-40);
        }
      }

      .arrow {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;

        .arrow-svg {
          display: block;
          border: solid var(--main-light-shade-4);
          border-width: 0 2px 2px 0;
          padding: 5px;
          transform: rotate(-135deg);
          cursor: pointer;
          margin-top: var(--spacing-24);
        }
      }
    }

    &.open {
      &:before {
        visibility: visible;
        opacity: 1;
      }

      .__filters-dropdown {
        max-height: calc(100% - 104px);
        display: flex;
        padding: var(--spacing-24);
        z-index: 1;
      }
    }
  }
}

@include bp($bp-phone) {
  .__filters {
    .__filters-dropdown-footer {
      .btn-primary {
        margin-right: auto;
      }
    }
  }
}
</style>
